import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import type { ITerminal } from "src/types/types";
import { S_CHANNELS_LIST_REQUEST_UPDATE, type TPaymentChannel } from "../channelList/channelData";
import type { TIntegration } from "../pages/IntegrationsList";
import { CHANNELS_LIMIT } from "./providersData";
import type { TPaymentProvider } from "./ProvidersList";

type TList<T> = {
	items: T[];
	/** No answer yet: the list shows placeholders instead of an empty state. */
	loading: boolean;
	busy: boolean;
	error?: IError;
	reload: () => void;
};

export type TProvidersDirectory = {
	providers: TList<TPaymentProvider>;
	channels: TList<TPaymentChannel> & { truncated: boolean };
	integrations: TList<TIntegration>;
	methods: TList<ITerminal>;
	reloadAll: () => void;
};

const asArray = <T>(data: unknown): T[] => (Array.isArray(data) ? data : data && typeof data === "object" ? [data as T] : []);

const useList = <T>(request: IAPIRequest): TList<T> => {
	const [response, reload, busy] = useAPI<T[]>(request);
	return {
		items: response?.error ? [] : asArray<T>(response?.data),
		loading: !response,
		busy,
		error: response?.error,
		reload: () => void reload(),
	};
};

/**
 * Everything the Providers tabs show, loaded once for the page. Channels carry their provider, method and
 * integration, so the other tabs derive their usage counts from the same list.
 */
export const useProvidersDirectory = (): TProvidersDirectory => {
	const providers = useList<TPaymentProvider>({ method: "payments/providers/list" });
	const channels = useList<TPaymentChannel>({ method: "payments/channels/list", data: {} });
	const integrations = useList<TIntegration>({ method: "integrations/get", data: {} });
	const methods = useList<ITerminal>({ method: "terminal/list", data: {} });

	// Channel changes can also change what the channel shows of its integration and beneficiary.
	S_CHANNELS_LIST_REQUEST_UPDATE.use([], () => {
		channels.reload();
		integrations.reload();
	});
	GD.S_TERMINALS_UPDATE.use([], () => {
		methods.reload();
		channels.reload();
	});

	return {
		providers,
		channels: { ...channels, truncated: channels.items.length >= CHANNELS_LIMIT },
		integrations,
		methods,
		reloadAll: () => {
			providers.reload();
			channels.reload();
			integrations.reload();
			methods.reload();
		},
	};
};
